import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import axios from 'axios';

// Create axios instance with default config
const instance = axios.create({
  withCredentials: true,
  baseURL: ''
});

// Flag to track if we're currently refreshing to prevent multiple refresh requests
let isRefreshing = false;
let refreshSubscribers = [];

// Helper to process queued requests after token refresh
const onRefreshed = (token) => {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (cb) => {
  refreshSubscribers.push(cb);
};

// Helper to handle authentication failure
const handleAuthFailure = () => {
  //log the failure
  console.error('Authentication failure: Refresh token invalid or expired.');
  localStorage.removeItem('isAuthenticated');
  window.location.reload();
};

// Add response interceptor to handle token refresh and offline state
instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If we're offline, reject immediately with a custom error
    if (!navigator.onLine) {
      return Promise.reject({ offline: true });
    }

    // If the request was to the login endpoint, just reject
    if (originalRequest.url && originalRequest.url.includes('/api/login')) {
      return Promise.reject(error);
    }
    
    // Don't retry if this is already a refresh token request
    if (originalRequest.url && originalRequest.url.includes('/api/refresh')) {
      console.error('Refresh token request failed. At url: ', originalRequest.url);
      isRefreshing = false;
      // If refresh token request fails, clear auth and reload
      if (error.response?.status === 401) {
        handleAuthFailure();
      }
      return Promise.reject(error);
    }

    // If the error is due to an expired token
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Wait for the token refresh
        return new Promise((resolve, reject) => {
          addRefreshSubscriber((token) => {
            if (!token) {
              reject(error);
            } else {
              resolve(instance(originalRequest));
            }
          });
        });
      }

      isRefreshing = true;
      originalRequest._retry = true;

      try {
        await instance.post('/api/refresh');
        isRefreshing = false;
        onRefreshed(true);
        return instance(originalRequest);
      } catch (refreshError) {
        console.log('Refresh token invalid or expired.');
        isRefreshing = false;
        onRefreshed(false);
        // Clear local auth state and reload to show login component
        handleAuthFailure();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Add request interceptor to handle CSRF tokens
instance.interceptors.request.use(
  async (config) => {
    // If we're offline, only allow GET requests
    if (!navigator.onLine && config.method !== 'get') {
      return Promise.reject({ offline: true });
    }
    
    // Set CSRF token based on the endpoint
    if (config.url && config.url.includes('/api/refresh')) {
      const csrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('csrf_refresh_token='));
      if (csrfToken) {
        config.headers['X-CSRF-TOKEN'] = decodeURIComponent(csrfToken.split('=')[1]);
      }
    } else if (config.method !== 'get') {
      const csrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('csrf_access_token='));
      if (csrfToken) {
        config.headers['X-CSRF-TOKEN'] = decodeURIComponent(csrfToken.split('=')[1]);
      }
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Export the configured axios instance
export default instance;

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// Register service worker
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/serviceWorker.js')
      .then(registration => {
        console.log('ServiceWorker registration successful with scope: ', registration.scope);
        
        // Register for background sync if available
        if ('sync' in registration) {
          // Check if we're online
          window.addEventListener('online', () => {
            registration.sync.register('sync-reading-progress')
              .catch(err => console.error('Background sync registration failed:', err));
          });
          
          // Register on page load if we're online
          if (navigator.onLine) {
            registration.sync.register('sync-reading-progress')
              .catch(err => console.error('Background sync registration failed:', err));
          }
        }
      })
      .catch(error => {
        console.error('ServiceWorker registration failed: ', error);
      });
  });
}